import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle, UserPlus, ArrowRight } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

// =====================================================================
// PublicPatientRegistration — patient-facing, unauthenticated sign-up.
//
// URL shape: /r/<token>   (token is the clinic's reusable QR/link)
//
// Flow:
//   1. Read /:token from the route.
//   2. public.get_registration_clinic(token) — anon RPC, returns the
//      clinic name + logo + enabled flag (derives clinic from the token
//      only; can never touch another clinic).
//   3. Patient fills name + mobile (required) and optional DOB / email /
//      address, then submits.
//   4. public.register_patient_via_token(token, profile, honeypot) creates
//      the patient instantly (flagged self-registered / unverified) plus a
//      'Not Submitted' medical-history row, and returns its submit token.
//   5. We hand the patient straight to the EXISTING medical-history form
//      at /medical-history/<medical_history_token>.
//
// A hidden honeypot field traps bots — server writes nothing if it's set.
// =====================================================================

const EMPTY_FORM = { full_name: "", phone: "", date_of_birth: "", email: "", address: "" };

export default function PublicPatientRegistration() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clinic, setClinic] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [honeypot, setHoneypot] = useState(""); // must stay empty for humans
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    supabase.rpc("get_registration_clinic", { p_token: token }).then(({ data, error }) => {
      if (!alive) return;
      setLoading(false);
      if (error) { setLoadError(error.message || "This link is invalid."); return; }
      setClinic(data);
    });
    return () => { alive = false; };
  }, [token]);

  function update(key, value) { setForm((f) => ({ ...f, [key]: value })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);
    if (!form.full_name.trim()) { setFormError("Full name is required."); return; }
    if (!form.phone.trim())     { setFormError("Mobile number is required."); return; }
    setSubmitting(true);
    const { data, error } = await supabase.rpc("register_patient_via_token", {
      p_token: token, p_profile: form, p_honeypot: honeypot,
    });
    setSubmitting(false);
    if (error) { setFormError(error.message || "Something went wrong. Please try again."); return; }
    if (data?.medical_history_token) {
      navigate(`/medical-history/${data.medical_history_token}`);
    } else {
      // Honeypot / no-op path — show a neutral confirmation rather than an error.
      setClinic((c) => ({ ...c, _done: true }));
    }
  }

  const clinicName = clinic?.clinic_name || "your dental clinic";

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-md mx-auto px-4 py-6 sm:py-10">
        <header className="mb-5 flex items-center gap-3">
          {clinic?.clinic_logo_url ? (
            <img
              src={clinic.clinic_logo_url}
              alt={clinicName}
              className="w-10 h-10 rounded-xl object-contain bg-white border border-slate-200 p-1"
            />
          ) : null}
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-amber-600">New patient sign-up</p>
            <p className="text-sm text-slate-700">{clinic?.clinic_name || "Your dental clinic"}</p>
          </div>
        </header>

        {loading ? (
          <Card>
            <div className="text-center py-10">
              <Loader2 className="w-5 h-5 mx-auto animate-spin text-amber-500" />
              <p className="mt-3 text-sm text-slate-500">Loading…</p>
            </div>
          </Card>
        ) : loadError ? (
          <Card>
            <div className="text-center py-8 px-4" data-testid="public-reg-error">
              <div className="w-12 h-12 rounded-full bg-rose-50 mx-auto flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-rose-500" />
              </div>
              <h2 className="mt-3 text-base font-semibold text-slate-900">This link isn't working.</h2>
              <p className="mt-2 text-sm text-slate-500">{loadError}</p>
              <p className="mt-4 text-xs text-slate-400">Please ask the front desk for a fresh link.</p>
            </div>
          </Card>
        ) : clinic?.enabled === false ? (
          <Card>
            <div className="text-center py-8 px-4" data-testid="public-reg-closed">
              <div className="w-12 h-12 rounded-full bg-slate-100 mx-auto flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-slate-400" />
              </div>
              <h2 className="mt-3 text-base font-semibold text-slate-900">Online sign-up is currently closed.</h2>
              <p className="mt-2 text-sm text-slate-500">Please see the front desk to register.</p>
            </div>
          </Card>
        ) : clinic?._done ? (
          <Card>
            <div className="text-center py-10 px-4" data-testid="public-reg-thanks">
              <h2 className="text-lg font-semibold text-slate-900">Thank you!</h2>
              <p className="mt-2 text-sm text-slate-500">Your details have been received. You can close this window.</p>
            </div>
          </Card>
        ) : (
          <Card>
            <form onSubmit={handleSubmit} className="px-5 py-5 space-y-4" data-testid="public-reg-form">
              <div>
                <h1 className="text-xl font-bold text-slate-900">Register as a new patient</h1>
                <p className="mt-1 text-sm text-slate-600">
                  Fill in your details below. It only takes a minute — next you'll complete a short
                  medical-history form.
                </p>
              </div>

              <Field label="Full name" required>
                <input
                  type="text" value={form.full_name} onChange={(e) => update("full_name", e.target.value)}
                  data-testid="public-reg-name" autoComplete="name"
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500"
                />
              </Field>
              <Field label="Mobile number" required>
                <input
                  type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)}
                  data-testid="public-reg-phone" autoComplete="tel"
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500"
                />
              </Field>
              <Field label="Date of birth">
                <input
                  type="date" value={form.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)}
                  data-testid="public-reg-dob"
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
                  data-testid="public-reg-email" autoComplete="email"
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500"
                />
              </Field>
              <Field label="Address">
                <input
                  type="text" value={form.address} onChange={(e) => update("address", e.target.value)}
                  data-testid="public-reg-address" autoComplete="street-address"
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500"
                />
              </Field>

              {/* Honeypot — hidden from humans; bots fill it and get silently dropped. */}
              <input
                type="text" tabIndex={-1} autoComplete="off" aria-hidden="true"
                value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
                className="hidden" data-testid="public-reg-honeypot"
              />

              {formError ? (
                <p className="text-sm text-rose-600" data-testid="public-reg-form-error">{formError}</p>
              ) : null}

              <button
                type="submit" disabled={submitting} data-testid="public-reg-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-40"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Continue to medical history
              </button>
              <p className="text-xs text-slate-400 text-center">
                Your information is private to {clinicName}.
              </p>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">
        {label}{required ? <span className="text-rose-500"> *</span> : null}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Card({ children }) {
  return <div className="bg-white rounded-2xl shadow-sm border border-slate-100">{children}</div>;
}
